import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Filter, Trash2, AlertTriangle, Download, FileText, FileSpreadsheet, ChevronDown, ChevronRight, X, RotateCcw, TrendingUp, Receipt, Car, Pencil } from "lucide-react";
import { exportCSV, exportExcel, exportPDF } from "../components/TableExport";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";

const raisonTypes = [
  { value: "demi_journee", label: "Demi-journee", color: "bg-amber-100 text-amber-800 border-amber-300" },
  { value: "repos", label: "Jour de repos", color: "bg-slate-100 text-slate-700 border-slate-300" },
  { value: "dette", label: "Dette (paiera apres)", color: "bg-red-100 text-red-800 border-red-300" },
  { value: "reparation", label: "Reparation deduite", color: "bg-blue-100 text-blue-800 border-blue-300" },
  { value: "amende", label: "Amende a regler", color: "bg-orange-100 text-orange-800 border-orange-300" },
  { value: "remboursement_dette", label: "Remboursement dette", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { value: "remboursement_amende", label: "Remboursement amende", color: "bg-teal-100 text-teal-800 border-teal-300" },
  { value: "autre", label: "Autre", color: "bg-slate-100 text-slate-600 border-slate-200" },
];

const getRaisonColor = (type) => raisonTypes.find(r => r.value === type)?.color || "bg-slate-100 text-slate-600";
const getRaisonLabel = (type) => raisonTypes.find(r => r.value === type)?.label || type;

function getYesterday() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split("T")[0];
}
function getToday() {
  return new Date().toISOString().split("T")[0];
}

export default function RecettesPage() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const [recettes, setRecettes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ chauffeur_id: "", vehicule_id: "", date_location: getYesterday(), date_paiement: getToday(), montant_attendu: "", montant_paye: "", notes: "" });
  const [raisons, setRaisons] = useState([]);
  const [filterDateDebut, setFilterDateDebut] = useState("");
  const [filterDateFin, setFilterDateFin] = useState("");
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fraisGestion, setFraisGestion] = useState(true);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [continueMode, setContinueMode] = useState(false);
  const [editRecette, setEditRecette] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));

  const isCurrentWeek = (dateStr) => {
    if (!dateStr) return false;
    const now = new Date();
    const day = now.getDay();
    const monday = new Date(now); monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1)); monday.setHours(0,0,0,0);
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6); sunday.setHours(23,59,59,999);
    const d = new Date(dateStr + "T12:00:00");
    return d >= monday && d <= sunday;
  };
  const canEdit = currentUser?.role === "admin" || currentUser?.role === "gestionnaire";

  const fetchData = async () => {
    try {
      let params = "";
      const qp = [];
      if (filterDateDebut) qp.push(`date_debut=${filterDateDebut}`);
      if (filterDateFin) qp.push(`date_fin=${filterDateFin}`);
      if (qp.length > 0) params = `?${qp.join("&")}`;
      const [rRes, vRes, dRes, aRes] = await Promise.all([
        api.get(`/recettes${params}`), api.get("/vehicles"), api.get("/users?role=chauffeur"), api.get("/assignments")
      ]);
      setRecettes(rRes.data); setVehicles(vRes.data); setDrivers(dRes.data); setAssignments(aRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filterDateDebut, filterDateFin]);

  const handleDriverChange = useCallback((chauffeurId) => {
    const driverAssignments = assignments.filter(a => a.chauffeur_id === chauffeurId && a.actif);
    if (driverAssignments.length > 0) {
      const a = driverAssignments[0];
      const vehicle = vehicles.find(v => v.id === a.vehicule_id);
      setForm(prev => ({
        ...prev,
        chauffeur_id: chauffeurId,
        vehicule_id: a.vehicule_id,
        montant_attendu: String(a.recette_par_jour || vehicle?.recette_journaliere || ""),
        date_location: getYesterday(),
        date_paiement: getToday(),
      }));
    } else {
      setForm(prev => ({ ...prev, chauffeur_id: chauffeurId }));
    }
  }, [assignments, vehicles]);

  const checkAndSubmit = async (e) => {
    e.preventDefault();
    if (!form.vehicule_id || !form.date_location) return;
    try {
      const res = await api.get(`/recettes/check-duplicate?vehicule_id=${form.vehicule_id}&date_location=${form.date_location}`);
      if (res.data.exists) {
        setDuplicateWarning(res.data);
        setShowDuplicateConfirm(true);
        return;
      }
    } catch (e) { /* ignore check errors, proceed */ }
    await submitRecette();
  };

  const submitRecette = async () => {
    try {
      if (editRecette) {
        await api.put(`/recettes/${editRecette.id}`, {
          ...form,
          montant_attendu: parseFloat(form.montant_attendu),
          montant_paye: parseFloat(form.montant_paye),
          raisons_difference: raisons,
          frais_gestion: fraisGestion,
        });
        toast.success("Recette modifiee avec succes");
        setEditRecette(null);
        setShowAdd(false);
        resetForm();
      } else {
        await api.post("/recettes", {
          ...form,
          montant_attendu: parseFloat(form.montant_attendu),
          montant_paye: parseFloat(form.montant_paye),
          raisons_difference: raisons,
          frais_gestion: fraisGestion,
        });
        toast.success("Recette enregistree avec succes");
        setShowDuplicateConfirm(false);
        setDuplicateWarning(null);
        if (continueMode) {
          resetForm();
        } else {
          setShowAdd(false);
          resetForm();
        }
      }
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openEditRecette = (r) => {
    setEditRecette(r);
    setForm({
      chauffeur_id: r.chauffeur_id || "",
      vehicule_id: r.vehicule_id || "",
      date_location: r.date_location || "",
      date_paiement: r.date_paiement || "",
      montant_attendu: r.montant_attendu || "",
      montant_paye: r.montant_paye || "",
      notes: r.notes || "",
    });
    setRaisons(r.raisons_difference || []);
    setFraisGestion(r.frais_gestion !== false);
    setShowAdd(true);
  };

  const resetForm = () => {
    setForm({ chauffeur_id: "", vehicule_id: "", date_location: getYesterday(), date_paiement: getToday(), montant_attendu: "", montant_paye: "", notes: "" });
    setRaisons([]);
    setFraisGestion(true);
    setDuplicateWarning(null);
  };

  const addRaison = () => setRaisons([...raisons, { type: "autre", montant: 0, description: "" }]);
  const updateRaison = (i, field, value) => { const r = [...raisons]; r[i][field] = field === "montant" ? parseFloat(value || 0) : value; setRaisons(r); };
  const removeRaison = (i) => setRaisons(raisons.filter((_, idx) => idx !== i));

  const getDriverName = (id) => { const d = drivers.find(dr => dr.id === id); return d ? `${d.prenom} ${d.nom}` : "-"; };
  const getVehicleImmat = (id) => { const v = vehicles.find(vh => vh.id === id); return v ? v.immatriculation : "-"; };

  const recetteExportCols = [
    { label: "Date location", key: "date_location" },
    { label: "Date paiement", key: "date_paiement" },
    { label: "Chauffeur", accessor: r => getDriverName(r.chauffeur_id) },
    { label: "Vehicule", accessor: r => getVehicleImmat(r.vehicule_id) },
    { label: "Attendu (Frc)", key: "montant_attendu" },
    { label: "Paye (Frc)", key: "montant_paye" },
    { label: "Difference (Frc)", key: "difference" },
    { label: "Statut", key: "statut" },
  ];

  const handleDelete = async (recetteId) => {
    if (!window.confirm("Supprimer cette recette ? Les dettes associees seront aussi supprimees.")) return;
    try {
      await api.delete(`/recettes/${recetteId}`);
      toast.success("Recette supprimee");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const diff = parseFloat(form.montant_paye || 0) - parseFloat(form.montant_attendu || 0);
  const hasDiff = form.montant_attendu !== "" && form.montant_paye !== "" && diff !== 0;

  // Client-side vehicle filtering
  const filteredRecettes = selectedVehicles.length > 0
    ? recettes.filter(r => selectedVehicles.includes(r.vehicule_id))
    : recettes;

  // Totaux
  const totalAttendu = filteredRecettes.reduce((s, r) => s + (r.montant_attendu || 0), 0);
  const totalPaye = filteredRecettes.reduce((s, r) => s + (r.montant_paye || 0), 0);

  const hasActiveFilters = filterDateDebut || filterDateFin || selectedVehicles.length > 0;

  const toggleVehicle = (vehicleId) => {
    setSelectedVehicles(prev =>
      prev.includes(vehicleId) ? prev.filter(id => id !== vehicleId) : [...prev, vehicleId]
    );
  };
  const selectAllVehicles = () => setSelectedVehicles([]);
  const resetAllFilters = () => {
    setFilterDateDebut("");
    setFilterDateFin("");
    setSelectedVehicles([]);
  };

  const vehicleFilterLabel = selectedVehicles.length === 0
    ? "Tous les vehicules"
    : selectedVehicles.length === 1
      ? getVehicleImmat(selectedVehicles[0])
      : `${selectedVehicles.length} vehicule(s)`;

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div data-testid="recettes-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Recettes</h1>
          <p className="text-sm text-slate-500 mt-1">{filteredRecettes.length} recette(s)</p>
        </div>
        <div className="flex items-center gap-3">
          <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={() => { resetForm(); setEditRecette(null); setShowAdd(true); }} data-testid="btn-add-recette">
            <Plus className="w-4 h-4 mr-2" />Enregistrer
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4" data-testid="recettes-filters">
        {/* Vehicle multi-select */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full sm:w-[220px] justify-between text-sm" data-testid="filter-vehicles">
              <div className="flex items-center gap-2 truncate">
                <Car className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="truncate">{vehicleFilterLabel}</span>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 ml-1" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[260px] p-0" align="start">
            <div className="p-3 border-b">
              <button
                type="button"
                className="flex items-center gap-2 w-full text-sm font-medium text-[#0F52BA] hover:underline"
                onClick={selectAllVehicles}
                data-testid="btn-select-all-vehicles"
              >
                Tous les vehicules
              </button>
            </div>
            <div className="max-h-[240px] overflow-y-auto p-2 space-y-1">
              {vehicles.map(v => (
                <label
                  key={v.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer text-sm"
                  data-testid={`vehicle-option-${v.id}`}
                >
                  <Checkbox
                    checked={selectedVehicles.includes(v.id)}
                    onCheckedChange={() => toggleVehicle(v.id)}
                  />
                  <span className="truncate">{v.immatriculation} - {v.marque} {v.modele}</span>
                </label>
              ))}
            </div>
            {selectedVehicles.length > 0 && (
              <div className="p-2 border-t">
                <Button variant="ghost" size="sm" className="w-full text-xs" onClick={selectAllVehicles} data-testid="btn-clear-vehicle-filter">
                  <X className="w-3 h-3 mr-1" />Effacer la selection
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>

        {/* Date range */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <Label className="text-xs text-slate-500 whitespace-nowrap">Du</Label>
            <Input type="date" value={filterDateDebut} onChange={e => setFilterDateDebut(e.target.value)} className="w-[140px] text-sm" data-testid="filter-date-debut" />
          </div>
          <div className="flex items-center gap-1">
            <Label className="text-xs text-slate-500 whitespace-nowrap">Au</Label>
            <Input type="date" value={filterDateFin} onChange={e => setFilterDateFin(e.target.value)} className="w-[140px] text-sm" data-testid="filter-date-fin" />
          </div>
        </div>

        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={resetAllFilters} data-testid="btn-reset-recettes-filters">
            <RotateCcw className="w-4 h-4 mr-1" />Reinitialiser
          </Button>
        )}

        {/* Export */}
        <div className="ml-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="btn-export-recettes">
                <Download className="w-4 h-4 mr-2" />Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportCSV(filteredRecettes, recetteExportCols, "recettes")} data-testid="export-recettes-csv"><FileText className="w-4 h-4 mr-2" />CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportExcel(filteredRecettes, recetteExportCols, "recettes")} data-testid="export-recettes-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF(filteredRecettes, recetteExportCols, "recettes", "Liste des recettes")} data-testid="export-recettes-pdf"><FileText className="w-4 h-4 mr-2" />PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Summary Totals */}
      <div className="grid grid-cols-2 gap-4 mb-6" data-testid="recettes-totals">
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-[#0F52BA]" />
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-900 font-heading" data-testid="total-attendu">{totalAttendu.toLocaleString("fr-FR")} Frc</p>
          <p className="text-xs text-slate-500 mt-1">Total montant attendu</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-emerald-600 font-heading" data-testid="total-paye">{totalPaye.toLocaleString("fr-FR")} Frc</p>
          <p className="text-xs text-slate-500 mt-1">Total montant paye</p>
        </div>
      </div>

      <div className="table-container">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold text-slate-600">Date location</TableHead>
              <TableHead className="font-semibold text-slate-600">Date paiement</TableHead>
              <TableHead className="font-semibold text-slate-600">Chauffeur</TableHead>
              <TableHead className="font-semibold text-slate-600">Vehicule</TableHead>
              <TableHead className="font-semibold text-slate-600">Attendu</TableHead>
              <TableHead className="font-semibold text-slate-600">Paye</TableHead>
              <TableHead className="font-semibold text-slate-600">Difference</TableHead>
              <TableHead className="font-semibold text-slate-600">Statut</TableHead>
              {canEdit && <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRecettes.map(r => {
              const hasRaisons = (r.raisons_difference || []).length > 0;
              const isExpanded = expandedRows[r.id];
              return (
                <>
                  <TableRow key={r.id} data-testid={`recette-row-${r.id}`} className={hasRaisons ? "cursor-pointer hover:bg-slate-50/80" : ""} onClick={() => hasRaisons && toggleRow(r.id)}>
                    <TableCell className="text-slate-700">
                      <div className="flex items-center gap-1.5">
                        {hasRaisons && (isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />)}
                        {new Date(r.date_location + "T12:00:00").toLocaleDateString("fr-FR")}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{r.date_paiement && !isNaN(new Date(r.date_paiement).getTime()) ? new Date(r.date_paiement + "T12:00:00").toLocaleDateString("fr-FR") : "-"}</TableCell>
                    <TableCell className="font-medium text-slate-900">{getDriverName(r.chauffeur_id)}</TableCell>
                    <TableCell className="text-slate-600">{getVehicleImmat(r.vehicule_id)}</TableCell>
                    <TableCell className="text-slate-600">{r.montant_attendu} Frc</TableCell>
                    <TableCell className="font-medium">{r.montant_paye} Frc</TableCell>
                    <TableCell>
                      <span className={r.difference < 0 ? "text-red-600 font-semibold" : r.difference > 0 ? "text-emerald-600 font-semibold" : "text-slate-600"}>
                        {r.difference > 0 ? "+" : ""}{r.difference} Frc
                      </span>
                      {hasRaisons && !isExpanded && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {r.raisons_difference.slice(0, 2).map((ra, idx) => (
                            <Badge key={idx} variant="outline" className={`text-[10px] px-1.5 py-0 ${getRaisonColor(ra.type)}`}>
                              {getRaisonLabel(ra.type)}
                            </Badge>
                          ))}
                          {r.raisons_difference.length > 2 && <span className="text-[10px] text-slate-400">+{r.raisons_difference.length - 2}</span>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell><Badge variant="outline" className={`status-${r.statut}`}>{r.statut === "paye" ? "Paye" : r.statut === "partiel" ? "Partiel" : "Impaye"}</Badge></TableCell>
                    {canEdit && (
                      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditRecette(r)} data-testid={`btn-edit-recette-${r.id}`}>
                            <Pencil className="w-4 h-4 text-[#0F52BA]" />
                          </Button>
                          {isAdmin && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(r.id)} data-testid={`btn-delete-recette-${r.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  {hasRaisons && isExpanded && (
                    <TableRow key={`${r.id}-detail`} data-testid={`recette-detail-${r.id}`}>
                      <TableCell colSpan={canEdit ? 9 : 8} className="bg-slate-50/70 py-3 px-6">
                        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Raisons de l'ecart</div>
                        <div className="space-y-1.5">
                          {(r.raisons_difference || []).map((ra, idx) => (
                            <div key={idx} className="flex items-center gap-3 text-sm">
                              <Badge variant="outline" className={`text-xs ${getRaisonColor(ra.type)}`}>{getRaisonLabel(ra.type)}</Badge>
                              {ra.description && <span className="text-slate-600">{ra.description}</span>}
                              {ra.montant !== undefined && ra.montant !== 0 && <span className="font-medium text-slate-900 ml-auto">{ra.montant} Frc</span>}
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })}
            {filteredRecettes.length === 0 && <TableRow><TableCell colSpan={canEdit ? 9 : 8} className="text-center text-slate-500 py-8">Aucune recette</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Add Recette Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="recette-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editRecette ? "Modifier la recette" : "Enregistrer une recette"}</DialogTitle></DialogHeader>
          <form onSubmit={checkAndSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Chauffeur</Label>
                <Select value={form.chauffeur_id} onValueChange={handleDriverChange}>
                  <SelectTrigger data-testid="select-recette-driver"><SelectValue placeholder="Choisir un chauffeur..." /></SelectTrigger>
                  <SelectContent>{drivers.filter(d => assignments.some(a => a.chauffeur_id === d.id && a.actif)).map(d => <SelectItem key={d.id} value={d.id}>{d.prenom} {d.nom}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vehicule (immatriculation)</Label>
                <Select value={form.vehicule_id} onValueChange={v => setForm({...form, vehicule_id: v})}>
                  <SelectTrigger data-testid="select-recette-vehicle"><SelectValue placeholder="Auto-rempli..." /></SelectTrigger>
                  <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.immatriculation} - {v.marque} {v.modele}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date de location</Label>
                <Input type="date" value={form.date_location} onChange={e => setForm({...form, date_location: e.target.value})} required data-testid="input-date-location" />
              </div>
              <div>
                <Label>Date de paiement</Label>
                <Input type="date" value={form.date_paiement} onChange={e => setForm({...form, date_paiement: e.target.value})} required data-testid="input-date-paiement" />
              </div>
              <div>
                <Label>Montant attendu (Frc)</Label>
                <Input type="number" step="0.01" value={form.montant_attendu} onChange={e => setForm({...form, montant_attendu: e.target.value})} required data-testid="input-montant-attendu" />
              </div>
              <div>
                <Label>Montant paye (Frc)</Label>
                <Input type="number" step="0.01" value={form.montant_paye} onChange={e => setForm({...form, montant_paye: e.target.value})} required data-testid="input-montant-paye" />
              </div>
            </div>

            {/* Difference indicator */}
            {hasDiff && (
              <div className={`rounded-lg p-3 text-sm font-medium ${diff < 0 ? "bg-red-50 text-red-700 border border-red-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>
                Difference : {diff > 0 ? "+" : ""}{diff.toFixed(2)} Frc {diff < 0 ? "(deficit)" : "(surplus)"}
              </div>
            )}

            {/* Raisons */}
            {hasDiff && (
              <div className={`rounded-lg p-4 border ${diff < 0 ? "border-red-200 bg-red-50/50" : "border-emerald-200 bg-emerald-50/50"}`}>
                <div className="flex items-center justify-between mb-3">
                  <Label className={`font-semibold ${diff < 0 ? "text-red-800" : "text-emerald-800"}`}>Raisons de la difference</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addRaison} data-testid="btn-add-raison">+ Raison</Button>
                </div>
                {raisons.map((r, i) => {
                  const rColor = getRaisonColor(r.type);
                  const isDebt = ["dette", "remboursement_dette", "remboursement_amende", "amende"].includes(r.type);
                  return (
                    <div key={i} className={`flex gap-2 mt-2 items-end p-2 rounded-md ${isDebt ? rColor : "bg-white"}`}>
                      <Select value={r.type} onValueChange={v => updateRaison(i, "type", v)}>
                        <SelectTrigger className="w-[200px]" data-testid={`select-raison-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{raisonTypes.map(t => (
                          <SelectItem key={t.value} value={t.value}>
                            <span className={`inline-block w-2 h-2 rounded-full mr-2 ${t.color.split(" ")[0]}`} />{t.label}
                          </SelectItem>
                        ))}</SelectContent>
                      </Select>
                      <Input type="number" placeholder="Montant" value={r.montant} onChange={e => updateRaison(i, "montant", e.target.value)} className="w-24" data-testid={`input-raison-montant-${i}`} />
                      <Input placeholder="Description" value={r.description} onChange={e => updateRaison(i, "description", e.target.value)} className="flex-1" data-testid={`input-raison-desc-${i}`} />
                      <Button type="button" variant="ghost" size="icon" className="shrink-0 text-red-500 hover:text-red-700" onClick={() => removeRaison(i)}>X</Button>
                    </div>
                  );
                })}
                {raisons.some(r => ["dette", "amende"].includes(r.type)) && (
                  <p className="text-xs text-red-600 mt-2 font-medium">Les dettes et amendes seront automatiquement ajoutees dans la gestion des dettes.</p>
                )}
                {raisons.some(r => ["remboursement_dette", "remboursement_amende"].includes(r.type)) && (
                  <p className="text-xs text-emerald-600 mt-2 font-medium">Les remboursements seront automatiquement deduits des dettes existantes.</p>
                )}
              </div>
            )}

            <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="input-notes" /></div>

            {/* Frais de gestion */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-indigo-50 border border-indigo-200" data-testid="frais-gestion-section">
              <Switch checked={fraisGestion} onCheckedChange={setFraisGestion} data-testid="switch-frais-gestion" />
              <div>
                <Label className="text-indigo-800">Frais de gestion (10%)</Label>
                <p className="text-[11px] text-indigo-600">
                  {form.montant_paye ? `${(parseFloat(form.montant_paye) * 0.10).toFixed(2)} Frc seront enregistres en reparation` : "10% du montant paye sera enregistre en tant que frais de gestion"}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 pt-2 border-t">
              {!editRecette ? (
                <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600" data-testid="toggle-continue-recette">
                  <Checkbox checked={continueMode} onCheckedChange={setContinueMode} />
                  <span>Enchainer les saisies</span>
                </label>
              ) : <div />}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => { setShowAdd(false); setEditRecette(null); }}>Annuler</Button>
                <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-recette">{editRecette ? "Modifier" : "Enregistrer"}</Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Duplicate Confirmation Dialog */}
      <Dialog open={showDuplicateConfirm} onOpenChange={setShowDuplicateConfirm}>
        <DialogContent data-testid="duplicate-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading text-amber-700">Recette deja existante</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 mb-2">Une recette existe deja pour ce vehicule a cette date.</p>
                {duplicateWarning && (
                  <div className="space-y-1 text-amber-700">
                    <p>Vehicule : <span className="font-medium">{getVehicleImmat(duplicateWarning.recette.vehicule_id)}</span></p>
                    <p>Date : <span className="font-medium">{new Date(duplicateWarning.recette.date_location + "T12:00:00").toLocaleDateString("fr-FR")}</span></p>
                    <p>Chauffeur : <span className="font-medium">{duplicateWarning.chauffeur_nom}</span></p>
                    <p>Montant paye : <span className="font-medium">{duplicateWarning.recette.montant_paye} Frc</span></p>
                  </div>
                )}
              </div>
            </div>
            <p className="text-sm text-slate-600">Souhaitez-vous quand meme enregistrer une nouvelle recette ?</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDuplicateConfirm(false)}>Annuler</Button>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => { setShowDuplicateConfirm(false); submitRecette(); }} data-testid="btn-confirm-duplicate">
                Confirmer l'enregistrement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
